/**
 * PdpReviewsSchemaIntegrator — fork of custom-reviews-schema-integrator.js.
 *
 * Injects individual review[] objects into the Product JSON-LD.
 *
 * CHANGED 2026-08-05 — aggregateRating is no longer computed here when the server
 * already emitted one.
 *
 * Why: the aggregate used to be derived from the FETCHED reviews, capped at
 * FETCH_LIMIT (100). The original note said "by Crea's counts are modest" — that is no
 * longer true. Freja carries 198 reviews, so this script was publishing
 * reviewCount: "100" and an average taken from only the first 100 of them.
 *
 * The section now renders aggregateRating in Liquid from the reviews.rating /
 * reviews.rating_count metafields, which hold the true store-wide totals and are
 * visible to crawlers that do not execute JavaScript. That value is authoritative and
 * this script must not clobber it. The fallback below only runs when the server emitted
 * nothing (product with no rating metafield), and it stays capped — flagged as such.
 *
 * review[] is still built here: individual reviews are only available from the Okendo
 * API at runtime, so there is no server-side source for them.
 */
class PdpReviewsSchemaIntegrator {
  constructor() {
    this.storeId = null;
    this.locale = (window.Shopify && window.Shopify.locale) || null;
    this.FETCH_LIMIT = 100; // fetch enough to count all reviews for the group
    this.REVIEWS_IN_SCHEMA = 25; // how many individual Review objects to emit
    this.cacheKeyPrefix = "pdp_okendo_cache_";
    this.cacheDuration = 5 * 60 * 1000;
    // Strip JSON-LD control characters (Liquid can leave stray ones) before parsing.
    this.controlChars = new RegExp("[\\u0000-\\u001F\\u007F-\\u009F]", "g");
  }

  initSettings() {
    const script = document.getElementById("oke-reviews-settings");
    if (!script) return false;
    try {
      const s = JSON.parse(script.textContent);
      if (s.subscriberId) this.storeId = s.subscriberId;
      if (s.locale && s.matchCustomerLocale === false) this.locale = s.locale;
      return true;
    } catch (e) {
      console.error("PdpReviewsSchemaIntegrator: failed to parse oke-reviews-settings", e);
      return false;
    }
  }

  init() {
    this.initSettings();
    if (!this.storeId) return;
    this.updateSchema("reviews-pdp-container");
  }

  async updateSchema(selector) {
    const el = document.querySelector(selector);
    const groupId = el && el.dataset ? el.dataset.okeReviewsGroupId : null;
    if (!groupId) {
      console.warn("PdpReviewsSchemaIntegrator: no group id found on", selector);
      return;
    }
    try {
      const data = await this.getReviewData(groupId);
      if (data && Array.isArray(data.reviews) && data.reviews.length) {
        this.inject(data.reviews);
      }
    } catch (e) {
      console.error("PdpReviewsSchemaIntegrator error:", e);
    }
  }

  async getReviewData(groupId) {
    const cacheKey = `${this.cacheKeyPrefix}${this.locale}_${groupId}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < this.cacheDuration) return data;
      } catch (e) {
        /* ignore bad cache */
      }
    }

    let url = `https://api.okendo.io/v1/stores/${this.storeId}/collections/${groupId}/reviews`;
    const params = [`limit=${this.FETCH_LIMIT}`];
    if (this.locale) params.push(`locale=${this.locale}`);
    url += `?${params.join("&")}`;

    const res = await fetch(url, { method: "GET", mode: "cors" });
    if (!res.ok) throw new Error(`Okendo API error: ${res.status}`);
    const result = await res.json();

    sessionStorage.setItem(cacheKey, JSON.stringify({ data: result, timestamp: Date.now() }));
    return result;
  }

  inject(reviews) {
    const schemaScript = document.querySelector(
      'script[type="application/ld+json"][data-schema="product"]'
    );
    if (!schemaScript) return;

    try {
      const clean = schemaScript.textContent.replace(this.controlChars, "").trim();
      const schema = JSON.parse(clean);

      const target =
        schema["@type"] === "Product" || schema["@type"] === "ProductGroup" ? schema : null;
      if (!target) return;

      // aggregateRating: the server-rendered value is authoritative — it uses the true
      // store totals, not the capped fetch. Only fill in when it is genuinely absent.
      if (!target.aggregateRating) {
        const count = reviews.length;
        const sum = reviews.reduce((a, r) => a + (Number(r.rating) || 0), 0);
        const avg = (sum / count).toFixed(1);

        target.aggregateRating = {
          "@type": "AggregateRating",
          ratingValue: avg,
          // NOTE: capped at FETCH_LIMIT. Only reached when the product has no
          // reviews.rating_count metafield, which should not happen for a product
          // that has reviews at all.
          reviewCount: String(count),
          bestRating: "5",
          worstRating: "1",
        };
      }

      // review[] — a subset is fine for Google. No server-side source exists for these.
      target.review = reviews.slice(0, this.REVIEWS_IN_SCHEMA).map((r) => ({
        "@type": "Review",
        author: {
          "@type": "Person",
          name: (r.reviewer && r.reviewer.displayName) || "Anonymous",
        },
        datePublished: (r.dateCreated || "").split("T")[0],
        reviewBody: r.body,
        name: r.title || "Product Review",
        reviewRating: {
          "@type": "Rating",
          ratingValue: String(r.rating),
          bestRating: "5",
          worstRating: "1",
        },
      }));

      schemaScript.textContent = JSON.stringify(schema);
    } catch (e) {
      console.error("PdpReviewsSchemaIntegrator: failed to inject schema", e);
    }
  }
}

new PdpReviewsSchemaIntegrator().init();